import { Currency, formatToDollar } from "@inrev/common";
import type { FC } from "react";
import type { PpChangeColorMode } from "../ui/FinancialDataGrid/types";

export type PeriodChangeValueType = "dollars" | "ratio" | "percent" | "days";

type PeriodChangeBadgeProps = {
	/** This period's value. */
	value: number | null;
	/** The period being compared against (the column to the right). */
	prevValue: number | null;
	/** How `value` is measured; controls delta formatting. */
	type?: PeriodChangeValueType;
	/**
	 * How the change is colored: "higherIsBetter" (default) renders an increase green, "lowerIsBetter"
	 * renders a decrease green (e.g. debt, A/R turnover), and "neutral" renders gray regardless of
	 * direction (favorability carries no signal).
	 */
	colorMode?: PpChangeColorMode;
};

/** Formats the absolute P/P change in the value's own units, used as the sign-flip fallback. */
const formatNativeDelta = (absDiff: number, type: PeriodChangeValueType): string | null => {
	switch (type) {
		case "ratio":
			return `${absDiff.toFixed(2)}x`;
		case "percent":
			return `${absDiff.toFixed(1)}%`;
		case "days":
			return `${Math.round(absDiff)}d`;
		default:
			return formatToDollar(absDiff, { abbreviate: true }) ?? null;
	}
};

/**
 * A favorable/unfavorable period-over-period change badge comparing {@link PeriodChangeBadgeProps.value}
 * against {@link PeriodChangeBadgeProps.prevValue}. Renders nothing when either value is missing,
 * the comparison value is zero (no meaningful percentage), or the values are equal (no change).
 *
 * The arrow points in the direction the value moved — up when it increased, down when it decreased. The
 * color signifies whether that change is favorable (green) or unfavorable (red), governed by
 * {@link PeriodChangeBadgeProps.colorMode}: most metrics are "higherIsBetter", but "lowerIsBetter" (e.g.
 * debt, A/R turnover) treats a decrease as favorable and "neutral" renders the change in gray with no
 * favorability. A percent change is meaningless across a sign flip, so the change is shown in the value's
 * native units instead.
 */
export const PeriodChangeBadge: FC<PeriodChangeBadgeProps> = ({
	value,
	prevValue,
	type = "dollars",
	colorMode = "higherIsBetter",
}) => {
	if (value === null || prevValue === null || prevValue === 0) {
		return null;
	}

	const diff = type === "dollars" ? Currency.subtract(value, prevValue) : value - prevValue;
	if (diff === 0) {
		return null;
	}

	const isFavorable = colorMode === "lowerIsBetter" ? diff <= 0 : diff >= 0;
	const isIncrease = diff > 0;
	const change =
		Math.sign(value) * Math.sign(prevValue) === -1
			? formatNativeDelta(Math.abs(diff), type)
			: `${Math.abs(Math.round((diff / Math.abs(prevValue)) * 100))}%`;
	if (change === null) {
		return null;
	}

	return (
		<span
			data-testid="period-change-badge"
			className={`text-xs font-medium tabular-nums shrink-0 ${colorMode === "neutral" ? "text-tertiary" : isFavorable ? "text-confirm" : "text-danger"}`}
		>
			{isIncrease ? "↑" : "↓"} {change}
		</span>
	);
};
